/**
 * Map runtime: builds a MapData into a Phaser scene (backgrounds → tile layers → foreground
 * occluders) and exposes world-pixel queries. Only type imports from Phaser: every Phaser call goes
 * through the scene object, so this file loads in Node for unit tests (with a fake scene) and the
 * queries are usable without any scene at all (`visuals: false`).
 *
 * Tileset resolution for `MapLayerData.tileset` ids, first match wins:
 *   1. `opts.tilesets[id]` (per-call texture override, e.g. a showcase swapping in dev art),
 *   2. `MapData.tilesets` declarations (self-contained chapter maps),
 *   3. `registerTileset()` registry (src/maps/tilesets.ts),
 *   4. a texture whose key equals the id.
 * A missing texture degrades to the labelled dev tileset with a console.warn — never a throw.
 *
 * Lights are exposed as data only (`runtime.lights`): the effects builder's LanternLayer renders
 * them (src/effects/layers.ts) so lighting stays in one place.
 */
import type Phaser from 'phaser';
import { Depth, GAME_H, GAME_W } from '@/core/config';
import type { MapData, MapExit, MapInteraction, MapLightData, MapSpawn, MapTilesetDecl } from '@/core/types/contracts';
import { CollisionGrid, DEFAULT_BODY, type BodySize } from './collision';
import { ensureDevTileset } from './devkit';
import { exitAt as findExit, firstRectAt, interactionsAt as findInteractions } from './queries';
import { validateMap } from './schema';
import { getTileset } from './tilesets';

export interface MapRuntimeOptions {
  /** Build display objects (default true). `false` gives a pure query object. */
  visuals?: boolean;
  /** Per-call tileset id → texture key overrides (win over MapData.tilesets and the registry). */
  tilesets?: Record<string, string>;
  /** Run the schema validator and warn on problems (default true). */
  validate?: boolean;
}

/**
 * Minimal runtime contract (the integrator stub's shape). Scenes may hand-roll a data-only fallback
 * object of this type, so everything beyond the stub is optional here; `createMapRuntime` returns
 * the fully populated `MapRuntimeFull`.
 */
export interface MapRuntime {
  data: MapData;
  /** world-pixel collision query */
  isBlocked(px: number, py: number): boolean;
  /** interactions whose rect contains the point */
  interactionsAt(px: number, py: number): MapInteraction[];
  /** exit rect containing the point, if any */
  exitAt(px: number, py: number): MapData['exits'][number] | null;
  destroy(): void;
  readonly grid?: CollisionGrid;
  readonly tilemap?: Phaser.Tilemaps.Tilemap | null;
  readonly layers?: Phaser.Tilemaps.TilemapLayer[];
  readonly backgrounds?: Phaser.GameObjects.GameObject[];
  readonly foregrounds?: Phaser.GameObjects.GameObject[];
  readonly lights?: MapLightData[];
  readonly worldWidth?: number;
  readonly worldHeight?: number;
  readonly tilesets?: Record<string, string>;
  isBlockedRect?(x: number, y: number, w: number, h: number): boolean;
  isBodyBlocked?(feetX: number, feetY: number, body?: BodySize): boolean;
  setSolid?(tx: number, ty: number, solid: boolean): void;
  getSpawn?(id: string): MapSpawn | null;
  defaultSpawn?(): MapSpawn;
  isWater?(px: number, py: number): boolean;
  isSheltered?(px: number, py: number): boolean;
  getLayer?(name: string): Phaser.Tilemaps.TilemapLayer | null;
}

/** What `createMapRuntime` actually returns: every query and display handle present. */
export interface MapRuntimeFull extends MapRuntime {
  readonly grid: CollisionGrid;
  readonly tilemap: Phaser.Tilemaps.Tilemap | null;
  readonly layers: Phaser.Tilemaps.TilemapLayer[];
  readonly backgrounds: Phaser.GameObjects.GameObject[];
  readonly foregrounds: Phaser.GameObjects.GameObject[];
  /** Light definitions for the world/effects builders (not rendered here). */
  readonly lights: MapLightData[];
  readonly worldWidth: number;
  readonly worldHeight: number;
  /** Tileset id → texture key actually used for each layer tileset (after fallbacks). */
  readonly tilesets: Record<string, string>;
  /** any solid tile under the half-open rect */
  isBlockedRect(x: number, y: number, w: number, h: number): boolean;
  /** feet-anchored body (default 20 × 12) */
  isBodyBlocked(feetX: number, feetY: number, body?: BodySize): boolean;
  /** runtime collision override (does not mutate MapData) */
  setSolid(tx: number, ty: number, solid: boolean): void;
  getSpawn(id: string): MapSpawn | null;
  /** `start` if present, else the first spawn. */
  defaultSpawn(): MapSpawn;
  isWater(px: number, py: number): boolean;
  isSheltered(px: number, py: number): boolean;
  /** Tile layer by `MapLayerData.name` (null without visuals or when the layer failed to build). */
  getLayer(name: string): Phaser.Tilemaps.TilemapLayer | null;
}

const DEPTH_BAND: Record<MapData['layers'][number]['depth'], number> = { ground: Depth.GROUND, detail: Depth.GROUND_DETAIL, overhead: Depth.OVERHEAD };

/** Pure part of tileset resolution (see file header); exported for tools and tests. */
export function resolveMapTileset(data: MapData, id: string, overrides?: Record<string, string>): MapTilesetDecl {
  const decl = data.tilesets?.find((t) => t.id === id) ?? getTileset(id) ?? { id, texture: id };
  const texture = overrides?.[id] ?? decl.texture;
  return { ...decl, id, texture };
}

/**
 * Pixels a background must span so it never leaves a gap at the far camera scroll: the view plus the
 * scrollable distance times the parallax factor (a p = 0 sky only needs the view size).
 */
export function backgroundCoverage(worldWidth: number, worldHeight: number, parallax: number): { w: number; h: number } {
  const p = Math.min(1, Math.max(0, parallax));
  return {
    w: Math.ceil(GAME_W + Math.max(0, worldWidth - GAME_W) * p),
    h: Math.ceil(GAME_H + Math.max(0, worldHeight - GAME_H) * p),
  };
}

/** Builds tile layers / backgrounds / foregrounds into the scene and returns runtime queries. */
export function createMapRuntime(scene: Phaser.Scene, data: MapData, opts: MapRuntimeOptions = {}): MapRuntimeFull {
  const visuals = opts.visuals ?? true;
  if (opts.validate ?? true) {
    const v = validateMap(data);
    for (const e of v.errors) console.warn(`[maps] ${data.id}: ${e}`);
  }
  const grid = CollisionGrid.fromMap(data);
  const worldWidth = grid.worldWidth, worldHeight = grid.worldHeight;
  const backgrounds: Phaser.GameObjects.GameObject[] = [];
  const foregrounds: Phaser.GameObjects.GameObject[] = [];
  const layers: Phaser.Tilemaps.TilemapLayer[] = [];
  const layerByName = new Map<string, Phaser.Tilemaps.TilemapLayer>();
  const tilesets: Record<string, string> = {};
  let tilemap: Phaser.Tilemaps.Tilemap | null = null;

  // tileset id → texture, before any "texture missing" fallback (so the result is meaningful without visuals too)
  for (const ld of data.layers) if (!(ld.tileset in tilesets)) tilesets[ld.tileset] = resolveMapTileset(data, ld.tileset, opts.tilesets).texture;

  if (visuals) {
    // ---- layered backgrounds (bottom → top), parallax through scrollFactor
    (data.background ?? []).forEach((bg, i) => {
      const depth = Depth.GROUND - 100 + i;
      if (scene.textures.exists(bg.key)) {
        const p = Math.min(1, Math.max(0, bg.parallax ?? 1));
        const need = backgroundCoverage(worldWidth, worldHeight, p);
        const frame = scene.textures.get(bg.key).get();
        const covers = frame.width >= need.w && frame.height >= need.h;
        const obj = covers
          ? scene.add.image(0, 0, bg.key)
          : scene.add.tileSprite(0, 0, need.w, need.h, bg.key);
        if (!covers) console.warn(`[maps] ${data.id}: background "${bg.key}" is ${frame.width}×${frame.height} but parallax ${p} needs ${need.w}×${need.h}; repeating it`);
        backgrounds.push(obj.setOrigin(0, 0).setScrollFactor(p, p).setDepth(depth));
      } else {
        console.warn(`[maps] ${data.id}: background texture "${bg.key}" missing; drawing flat fallback`);
        backgrounds.push(scene.add.rectangle(0, 0, worldWidth, worldHeight, 0x223344).setOrigin(0, 0).setDepth(depth));
      }
    });

    // ---- tile layers (classic TilemapLayer: multi-tileset capable, Canvas fallback, no manual regen)
    if (data.layers.length > 0) {
      tilemap = scene.make.tilemap({ tileWidth: data.tileSize, tileHeight: data.tileSize, width: data.width, height: data.height });
      const sets = new Map<string, { set: Phaser.Tilemaps.Tileset; firstgid: number }>();
      let nextGid = 0;
      const tilesetFor = (id: string) => {
        const cached = sets.get(id);
        if (cached) return cached;
        const def = resolveMapTileset(data, id, opts.tilesets);
        let texture = def.texture;
        if (!scene.textures.exists(texture)) {
          console.warn(`[maps] ${data.id}: tileset "${id}" texture "${texture}" missing; using dev tileset`);
          texture = ensureDevTileset(scene);
        }
        tilesets[id] = texture;
        const set = tilemap!.addTilesetImage(id, texture, def.tileWidth ?? data.tileSize, def.tileHeight ?? data.tileSize, def.margin ?? 0, def.spacing ?? 0, nextGid);
        if (!set) throw new Error(`[maps] ${data.id}: could not add tileset "${id}"`);
        const entry = { set, firstgid: nextGid };
        nextGid += Math.max(1, set.total);
        sets.set(id, entry);
        return entry;
      };
      data.layers.forEach((ld, i) => {
        const { set, firstgid } = tilesetFor(ld.tileset);
        const layer = tilemap!.createBlankLayer(ld.name, set, 0, 0, data.width, data.height, data.tileSize, data.tileSize);
        if (!layer) { console.warn(`[maps] ${data.id}: layer "${ld.name}" could not be created (duplicate name?)`); return; }
        let overflow = 0;
        const rows: number[][] = [];
        for (let ty = 0; ty < data.height; ty++) {
          const row: number[] = [];
          for (let tx = 0; tx < data.width; tx++) {
            const v = ld.data[ty * data.width + tx] ?? -1;
            if (v < 0) row.push(-1);
            else if (v >= set.total) { overflow++; row.push(-1); }
            else row.push(v + firstgid);
          }
          rows.push(row);
        }
        if (overflow) console.warn(`[maps] ${data.id}: layer "${ld.name}" has ${overflow} tile indices beyond tileset "${ld.tileset}" (${set.total} tiles)`);
        layer.putTilesAt(rows, 0, 0, false);
        layer.setDepth(DEPTH_BAND[ld.depth] + i);
        layers.push(layer);
        layerByName.set(ld.name, layer);
      });
    }

    // ---- foreground occluders, depth-sorted by their ground contact line
    for (const fg of data.foreground ?? []) {
      const depth = Depth.WORLD_BASE + (fg.depthY ?? fg.y);
      if (scene.textures.exists(fg.key)) {
        foregrounds.push(scene.add.image(fg.x, fg.y, fg.key).setOrigin(0.5, 1).setDepth(depth));
      } else {
        console.warn(`[maps] ${data.id}: foreground texture "${fg.key}" missing; drawing marker`);
        foregrounds.push(scene.add.rectangle(fg.x, fg.y, 32, 48, 0xff00ff, 0.6).setOrigin(0.5, 1).setDepth(depth));
      }
    }
  }

  let destroyed = false;
  const destroy = () => {
    if (destroyed) return;
    destroyed = true;
    scene.events.off('shutdown', destroy);
    for (const o of [...backgrounds, ...foregrounds]) o.destroy();
    backgrounds.length = 0; foregrounds.length = 0;
    if (tilemap) { tilemap.destroy(); tilemap = null; } // destroys its TilemapLayer game objects too
    layers.length = 0;
    layerByName.clear();
  };
  if (visuals) scene.events.once('shutdown', destroy);

  return {
    data,
    grid,
    get tilemap() { return tilemap; },
    layers,
    backgrounds,
    foregrounds,
    lights: data.lights ?? [],
    worldWidth,
    worldHeight,
    tilesets,
    isBlocked: (px, py) => grid.isBlocked(px, py),
    isBlockedRect: (x, y, w, h) => grid.isBlockedRect(x, y, w, h),
    isBodyBlocked: (fx, fy, body = DEFAULT_BODY) => grid.isBodyBlocked(fx, fy, body),
    setSolid: (tx, ty, solid) => grid.setSolid(tx, ty, solid),
    interactionsAt: (px, py) => findInteractions(data.interactions, px, py),
    exitAt: (px, py): MapExit | null => findExit(data.exits, px, py),
    getSpawn: (id) => data.spawns.find((s) => s.id === id) ?? null,
    defaultSpawn: () => {
      const s = data.spawns.find((sp) => sp.id === 'start') ?? data.spawns[0];
      if (!s) throw new Error(`[maps] ${data.id}: map has no spawns`);
      return s;
    },
    isWater: (px, py) => firstRectAt(data.water ?? [], px, py) !== null,
    isSheltered: (px, py) => firstRectAt(data.rainShelter ?? [], px, py) !== null,
    getLayer: (name) => layerByName.get(name) ?? null,
    destroy,
  };
}

/** Registry of map data keyed by id; chapter builders register their JSON here. */
export const MAPS: Record<string, MapData> = {};

export function getMap(id: string): MapData {
  const m = MAPS[id];
  if (!m) throw new Error(`unknown map ${id}`);
  return m;
}

export function hasMap(id: string): boolean { return id in MAPS; }

/** Validates and registers a map; returns the validation so callers can surface warnings. */
export function registerMap(data: MapData) {
  const v = validateMap(data);
  if (!v.ok) console.warn(`[maps] registering "${data.id}" with ${v.errors.length} schema error(s):\n  - ${v.errors.join('\n  - ')}`);
  MAPS[data.id] = data;
  return v;
}
